import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Checklist from '../components/Checklist';
import { ScheduleModal, getMbHex, getMbDarkBg, getMbTextColor, getContrastTitle } from './Schedule';
import { DEFECT_TYPES } from '../constants';
import { useToast } from '../context/ToastContext';
import TodayCard from '../measuring/TodayCard';
import { NextLorries } from './Truck';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function uploadPhoto(file, bucket) {
  const ext      = file.name.split('.').pop() || 'jpg';
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/${bucket}/${filename}`,
    { method: 'POST', headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }, body: file }
  );
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || 'Upload failed'); }
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${filename}`;
}

function formatElapsed(startedAt) {
  const ms = Date.now() - new Date(startedAt).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatCarryDate(startedAt) {
  const d = new Date(startedAt);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (d >= yesterday && d < today) return 'yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ── MAIN PAGE ─────────────────────────────────────────────────
export default function Today() {
  const { toast } = useToast();
  const [tab, setTab]             = useState('today');
  const [jobs, setJobs]           = useState([]);
  const [schedule, setSchedule]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showChecklist, setShowChecklist]     = useState(null);
  const [showEndModal, setShowEndModal]       = useState(null);
  const [detailJob, setDetailJob]             = useState(null);
  const [expandedJob, setExpandedJob]         = useState(null);
  const [jobWeights, setJobWeights]           = useState({});
  const [jobScraps, setJobScraps]             = useState({});
  const [jobInspections, setJobInspections]   = useState({});
  const [weightInputs, setWeightInputs]       = useState({});
  const [scrapInputs, setScrapInputs]         = useState({});
  const [weightModal, setWeightModal]         = useState(null); // job object
  const [inspectModal, setInspectModal]       = useState(null); // job object
  const [scrapModal, setScrapModal]           = useState(null); // job object
  const [showJobModal, setShowJobModal]       = useState(false);
  const [modalResources, setModalResources]   = useState(null);
  const [colours, setColours]                 = useState([]);
  const [jobPlanTotals, setJobPlanTotals]     = useState({});
  const [startPrompt, setStartPrompt]         = useState(null); // sched entry awaiting setter name
  const [loadError, setLoadError]             = useState('');

  useEffect(() => {
    loadData();
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const iv = setInterval(() => loadData(false), 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') loadData(false); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, []); // eslint-disable-line

  async function loadData(showSpinner = true) {
    if (showSpinner) setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    try {
      const [jobsRes, schedRes, coRes] = await Promise.all([
        api.get('/api/v1/jobs'),
        api.get(`/api/v1/schedule?date=${today}`).catch(() => ({ data: [] })),
        api.get('/api/v1/colours').catch(() => ({ data: [] })),
      ]);
      const allJobs = jobsRes.data;
      setJobs(allJobs);
      setSchedule(schedRes.data);
      setColours(coRes.data);

      const running = allJobs.filter(j => j.status === 'running');
      const [wResults, sResults, iResults, ptResults] = await Promise.all([
        Promise.all(running.map(j =>
          api.get(`/api/v1/jobs/${j.id}/weights`).then(r => [j.id, r.data]).catch(() => [j.id, []])
        )),
        Promise.all(running.map(j =>
          api.get(`/api/v1/jobs/${j.id}/scrap`).then(r => [j.id, r.data]).catch(() => [j.id, []])
        )),
        Promise.all(running.map(j =>
          api.get(`/api/v1/jobs/${j.id}/inspections`).then(r => [j.id, r.data]).catch(() => [j.id, []])
        )),
        Promise.all(running.map(j =>
          api.get(`/api/v1/jobs/${j.id}/plan-totals`).then(r => [j.id, r.data]).catch(() => [j.id, null])
        )),
      ]);
      setJobWeights(Object.fromEntries(wResults));
      setJobScraps(Object.fromEntries(sResults));
      setJobInspections(Object.fromEntries(iResults));
      setJobPlanTotals(Object.fromEntries(ptResults));
    } catch (e) {
      if (showSpinner) setLoadError(e.response?.data?.error || e.message || 'Failed to load data');
    }
    setLoading(false);
  }

  function logWeight(job) {
    const inp = weightInputs[job.id] || {};
    if (!inp.weight) return;
    const w = parseFloat(inp.weight);
    const drift = job.target_weight_g ? parseFloat((w - job.target_weight_g).toFixed(2)) : null;
    api.post(`/api/v1/jobs/${job.id}/weights`, { weight_g: w, drift_g: drift }).then(r => {
      setJobWeights(p => ({ ...p, [job.id]: [r.data, ...(p[job.id] || [])] }));
      setWeightInputs(p => ({ ...p, [job.id]: { weight: '', shot: '' } }));
    });
  }

  function logScrap(job) {
    const inp = scrapInputs[job.id] || {};
    if (!inp.count) return;
    const reason = inp.reason === 'Other' ? (inp.other || 'Other') : inp.reason;
    api.post(`/api/v1/jobs/${job.id}/scrap`, { count: parseInt(inp.count), reason }).then(r => {
      setJobScraps(p => ({ ...p, [job.id]: [r.data, ...(p[job.id] || [])] }));
      setScrapInputs(p => ({ ...p, [job.id]: { count: '', reason: '', other: '' } }));
    });
  }

  function startScheduled(sched) {
    setStartPrompt(sched);
  }

  function confirmStart(setterName) {
    const sched = startPrompt;
    // Sent with the version this screen saw: a run the director moved since
    // the page loaded is refused, not started in the wrong place (046).
    const seen = sched.version != null ? { headers: { 'x-expect': JSON.stringify({ [sched.id]: sched.version }) } } : {};
    api.post(`/api/v1/schedule/${sched.id}/start`, { setter_name: setterName || null }, seen)
      .then(() => { setStartPrompt(null); loadData(); })
      .catch(err => {
        const d = err.response?.data;
        toast.error(d?.error || err.message || 'Failed to start job');
        if (d?.stale) { setStartPrompt(null); loadData(); }
      });
  }

  function archiveJob(job) {
    api.post(`/api/v1/jobs/${job.id}/archive`).then(() => loadData());
  }

  function acknowledgeChange(job) {
    api.post(`/api/v1/jobs/${job.id}/acknowledge-change`).then(() => loadData());
  }

  function openJobModal() {
    setShowJobModal(true);
    if (!modalResources) {
      Promise.all([
        api.get('/api/v1/machines'),
        api.get('/api/v1/moulds'),
        api.get('/api/v1/parts'),
        api.get('/api/v1/recipes'),
      ]).then(([m, mo, p, r]) => setModalResources({
        machines: m.data.filter(x => x.status !== 'archived'),
        moulds:   mo.data.filter(x => x.status !== 'archived'),
        parts:    p.data,
        recipes:  r.data.filter(x => x.status === 'active'),
      })).catch(() => { toast.error('Failed to load form data — please try again'); setShowJobModal(false); });
    }
  }

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const carryOvers     = jobs.filter(j => j.status === 'running' && new Date(j.started_at) < todayStart);
  const runningToday   = jobs.filter(j => j.status === 'running' && new Date(j.started_at) >= todayStart);
  const completedToday = jobs.filter(j =>
    j.status === 'completed' && !j.archived && j.ended_at && new Date(j.ended_at) >= todayStart
  );
  const scheduledPending = schedule.filter(sc => sc.status === 'scheduled');
  const allRunning = [...carryOvers, ...runningToday];

  return (
    <div style={s.page}>
      {loadError && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{loadError}</span>
          <button onClick={() => setLoadError('')} style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>×</button>
        </div>
      )}
      {/* New job modal */}
      {showJobModal && (
        modalResources
          ? <ScheduleModal
              job={null}
              {...modalResources}
              hideDateField={true}
              onSaved={async (schedId) => {
                if (schedId) {
                  await api.post(`/api/v1/schedule/${schedId}/start`, {}).catch(err => {
                    toast.error(err.response?.data?.error || 'Job saved but failed to start — check the schedule');
                  });
                }
                setShowJobModal(false);
                loadData();
              }}
              onClose={() => setShowJobModal(false)}
            />
          : <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, color: '#9ca3af', fontSize: '13px', fontFamily: 'system-ui' }}>
              Loading...
            </div>
      )}

      {/* Setter name prompt */}
      {startPrompt && (
        <SetterNamePrompt
          sched={startPrompt}
          onConfirm={confirmStart}
          onCancel={() => setStartPrompt(null)}
        />
      )}

      {/* Weight modal */}
      {weightModal && (
        <WeightModal
          job={weightModal}
          weights={jobWeights[weightModal.id] || []}
          onSubmit={(weight_g, drift_g) => {
            api.post(`/api/v1/jobs/${weightModal.id}/weights`, { weight_g, drift_g }).then(r => {
              setJobWeights(p => ({ ...p, [weightModal.id]: [r.data, ...(p[weightModal.id] || [])] }));
              setWeightModal(null);
            });
          }}
          onClose={() => setWeightModal(null)}
        />
      )}

      {/* Inspect modal */}
      {inspectModal && (
        <InspectModal
          job={inspectModal}
          onSubmit={payload => {
            api.post(`/api/v1/jobs/${inspectModal.id}/inspections`, payload).then(r => {
              setJobInspections(p => ({ ...p, [inspectModal.id]: [r.data, ...(p[inspectModal.id] || [])] }));
              setInspectModal(null);
            });
          }}
          onClose={() => setInspectModal(null)}
        />
      )}

      {/* Scrap modal */}
      {scrapModal && (
        <ScrapModal
          job={scrapModal}
          onSubmit={payload => {
            api.post(`/api/v1/jobs/${scrapModal.id}/scrap`, payload).then(r => {
              setJobScraps(p => ({ ...p, [scrapModal.id]: [r.data, ...(p[scrapModal.id] || [])] }));
              setScrapModal(null);
            });
          }}
          onClose={() => setScrapModal(null)}
        />
      )}

      {/* Checklist modal */}
      {showChecklist && (
        <div style={s.overlay} onClick={() => setShowChecklist(null)}>
          <div style={{ ...s.modal, maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>
                {showChecklist.type === 'machine' ? `⚙️ Machine — ${showChecklist.entityName}` :
                 showChecklist.type === 'mould'   ? `🔲 Mould — ${showChecklist.entityName}` :
                 `🔍 FAI — ${showChecklist.entityName}`}
              </div>
              <button style={s.closeBtn} onClick={() => setShowChecklist(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <Checklist
                type={showChecklist.type}
                entityId={showChecklist.entityId}
                entityName={showChecklist.entityName}
                jobId={showChecklist.jobId}
              />
            </div>
          </div>
        </div>
      )}

      {/* End job modal */}
      {showEndModal && (
        <EndJobModal
          job={showEndModal}
          weights={jobWeights[showEndModal.id] || []}
          scraps={jobScraps[showEndModal.id] || []}
          totalScrap={(jobScraps[showEndModal.id] || []).reduce((sum, sc) => sum + sc.count, 0)}
          planTotals={jobPlanTotals[showEndModal.id] || null}
          onCancel={() => setShowEndModal(null)}
          onEnded={() => { setShowEndModal(null); setExpandedJob(null); loadData(); }}
        />
      )}

      {/* Job detail modal */}
      {detailJob && <JobDetailModal job={detailJob} onClose={() => setDetailJob(null)} />}

      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Today</h1>
          <p style={s.sub}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <NextLorries />
          <button style={s.startBtn} onClick={openJobModal}>+ Start New Job</button>
        </div>
      </div>

      {/* Measuring given to me today -- only when there is some */}
      <TodayCard />

      {/* Tabs */}
      <div style={s.tabRow}>
        <button style={{ ...s.tabBtn, ...(tab === 'today'   ? s.tabBtnActive : {}) }} onClick={() => setTab('today')}>Today</button>
        <button style={{ ...s.tabBtn, ...(tab === 'archive' ? s.tabBtnActive : {}) }} onClick={() => setTab('archive')}>Archive</button>
      </div>

      {tab === 'archive' && <ArchiveView onSelect={setDetailJob} />}

      {tab === 'today' && (
        loading ? <div style={s.center}>Loading...</div> : (
          <>
            {/* Scheduled section — hidden if nothing pending */}
            {scheduledPending.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionLabel}>Scheduled for today</div>
                {scheduledPending.map(sched => (
                  <ScheduledCard key={sched.id} sched={sched} colours={colours} onStart={() => startScheduled(sched)} />
                ))}
              </div>
            )}

            {/* Currently Running */}
            <div style={s.section}>
              <div style={s.sectionLabel}>
                Currently Running
                {allRunning.length > 0 && <span style={s.countBadge}>{allRunning.length}</span>}
              </div>
              {allRunning.length === 0 ? (
                <div style={s.emptyBox}>
                  <div style={{ fontSize: '36px', color: '#2a3040', marginBottom: '12px' }}>▶</div>
                  <div style={s.emptyText}>Nothing running right now</div>
                  <div style={s.emptySub}>Start a new job or kick off a scheduled run</div>
                </div>
              ) : (
                allRunning.map(job => (
                  <RunningJobCard
                    key={job.id}
                    job={job}
                    colours={colours}
                    isCarryOver={new Date(job.started_at) < todayStart}
                    planTotals={jobPlanTotals[job.id] || null}
                    expanded={expandedJob === job.id}
                    onToggle={() => setExpandedJob(prev => prev === job.id ? null : job.id)}
                    weights={jobWeights[job.id] || []}
                    scraps={jobScraps[job.id] || []}
                    inspections={jobInspections[job.id] || []}
                    weightInput={weightInputs[job.id] || {}}
                    scrapInput={scrapInputs[job.id] || {}}
                    onWeightChange={(f, v) => setWeightInputs(p => ({ ...p, [job.id]: { ...(p[job.id] || {}), [f]: v } }))}
                    onScrapChange={(f, v) => setScrapInputs(p => ({ ...p, [job.id]: { ...(p[job.id] || {}), [f]: v } }))}
                    onLogWeight={() => logWeight(job)}
                    onLogScrap={() => logScrap(job)}
                    onOpenWeightModal={() => setWeightModal(job)}
                    onOpenInspectModal={() => setInspectModal(job)}
                    onOpenScrapModal={() => setScrapModal(job)}
                    onChecklist={type => setShowChecklist({
                      type,
                      entityId:   type === 'machine' ? job.machine_id : type === 'mould' ? job.mould_id : job.recipe_id,
                      entityName: type === 'machine' ? job.machines?.name : type === 'mould' ? job.moulds?.ref : job.moulds?.name,
                      jobId: job.id,
                    })}
                    onEnd={() => setShowEndModal(job)}
                    onAcknowledge={() => acknowledgeChange(job)}
                  />
                ))
              )}
            </div>

            {/* Completed Today — hidden if nothing */}
            {completedToday.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionLabel}>
                  Completed Today
                  <span style={s.countBadge}>{completedToday.length}</span>
                </div>
                {completedToday.map(job => (
                  <CompletedCard
                    key={job.id}
                    job={job}
                    colours={colours}
                    onClick={() => setDetailJob(job)}
                    onArchive={() => archiveJob(job)}
                  />
                ))}
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}

// ── RUNNING JOB CARD ──────────────────────────────────────────
function RunningJobCard({
  job, isCarryOver, expanded, onToggle, colours, planTotals,
  weights, scraps, inspections, weightInput, scrapInput,
  onWeightChange, onScrapChange, onLogWeight, onLogScrap,
  onOpenWeightModal, onOpenInspectModal, onOpenScrapModal,
  onChecklist, onEnd, onAcknowledge,
}) {
  const [elapsed, setElapsed] = useState(formatElapsed(job.started_at));
  const notifFiredRef = useRef(false);

  useEffect(() => {
    const iv = setInterval(() => setElapsed(formatElapsed(job.started_at)), 30000);
    return () => clearInterval(iv);
  }, [job.started_at]);

  const lastWeight = weights[0]?.weight_g;
  const totalScrap = scraps.reduce((sum, sc) => sum + sc.count, 0);
  const driftOk = lastWeight && job.target_weight_g
    ? Math.abs(lastWeight - job.target_weight_g) <= (job.weight_tolerance_g || 0.5)
    : true;

  const cavities = job.moulds?.cavities || 1;
  const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
  const shiftStart = new Date(job.started_at) > todayMidnight ? new Date(job.started_at) : todayMidnight;
  const elapsedTodaySec = (Date.now() - shiftStart.getTime()) / 1000;
  const expectedToday = job.target_cycle_s && elapsedTodaySec > 0
    ? Math.floor(elapsedTodaySec / job.target_cycle_s * cavities)
    : null;

  const intervalMin = job.parts?.weight_log_interval_minutes || 60;
  const lastLoggedAt = weights[0]?.logged_at;
  const minutesSinceLast = lastLoggedAt
    ? Math.floor((Date.now() - new Date(lastLoggedAt).getTime()) / 60000)
    : null;
  const weightOverdue = minutesSinceLast !== null && minutesSinceLast > intervalMin;

  useEffect(() => {
    if (weightOverdue && !notifFiredRef.current) {
      notifFiredRef.current = true;
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Weight log overdue', {
          body: `${job.parts?.name || job.moulds?.ref || 'Job'} — log weight now`,
          tag: `weight-${job.id}`,
        });
      }
    }
    if (!weightOverdue) notifFiredRef.current = false;
  }, [weightOverdue, job.id, job.parts, job.moulds]);

  const mbHex      = getMbHex(job.recipe, colours);
  const mbBg       = mbHex ? getMbDarkBg(mbHex)     : null;
  const mbText     = mbHex ? getMbTextColor(mbHex)   : null;
  const titleColor = getContrastTitle(mbHex);

  return (
    <div style={{ ...s.runCard, background: mbBg || '#1e2329', borderColor: isCarryOver ? '#92400e' : '#2a3040', borderLeftColor: isCarryOver ? '#f59e0b' : '#14b8a6', borderLeftWidth: '3px' }}>
      {/* Clickable header */}
      <div style={s.runCardTop} onClick={onToggle}>
        <div style={{ flex: 1 }}>
          {isCarryOver && (
            <div style={s.carryBadge}>Carry-over from {formatCarryDate(job.started_at)}</div>
          )}
          <div style={{ ...s.runTitle, color: titleColor }}>
            {[job.parts?.name, job.order_number].filter(Boolean).join(' — ') || [job.moulds?.ref, job.moulds?.name].filter(Boolean).join(' — ') || '—'}
          </div>
          <div style={{ ...s.runSub, color: mbText || undefined }}>
            {job.machines?.name} · {job.recipe?.name || job.material?.name}
            {job.setter_name && <span style={{ marginLeft: '8px', fontSize: '11px', color: mbText || '#9ca3af' }}>· {job.setter_name}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
          <div style={s.runningBadge}>● Running</div>
          <div style={s.chevron}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>

      {/* Weight overdue badge */}
      {weightOverdue && (
        <div style={s.weightDueBadge}>⚖ Weight log overdue — {minutesSinceLast}m ago</div>
      )}

      {/* Stats row — always visible */}
      <div style={s.statsRow}>
        <StatBox label="Running" value={elapsed} />
        <StatBox label="Weights" value={weights.length} />
        <StatBox
          label="Last weight"
          value={lastWeight ? `${Number(lastWeight).toLocaleString()}g` : '—'}
          valueColor={lastWeight ? (driftOk ? '#86efac' : '#fca5a5') : '#6b7280'}
        />
        <StatBox label="Scrap" value={totalScrap} valueColor={totalScrap > 0 ? '#fca5a5' : '#86efac'} />
      </div>

      {/* Production progress */}
      {(() => {
        const orderTarget = planTotals?.target_parts || job.target_parts;
        const orderMade   = planTotals?.total_good_parts ?? null;
        const isMultiShift = planTotals?.plan_id != null;
        const remaining   = orderTarget && orderMade != null ? orderTarget - orderMade : null;
        if (!orderTarget && expectedToday == null) return null;
        return (
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a3040', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {orderTarget && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#9ca3af' }}>
                    {isMultiShift ? `Order progress (${planTotals.completed_shifts}/${planTotals.total_shifts} shifts)` : 'Order target'}
                  </span>
                  <span style={{ fontWeight: '700', color: '#f0f2f5' }}>
                    {orderMade != null ? `${orderMade.toLocaleString()} / ` : ''}{orderTarget.toLocaleString()} pcs
                    {remaining != null && remaining > 0 && <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '11px' }}> · {remaining.toLocaleString()} remaining</span>}
                  </span>
                </div>
                {orderMade != null && orderTarget && (
                  <div style={{ background: '#0f1117', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '4px', background: '#14b8a6', width: `${Math.min(100, Math.round(orderMade / orderTarget * 100))}%` }} />
                  </div>
                )}
              </div>
            )}
            {expectedToday != null && (
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                Expected today: <strong style={{ color: '#9ca3af' }}>{expectedToday.toLocaleString()} pcs</strong>
                {orderTarget && expectedToday && <span> (target {Math.round(expectedToday / orderTarget * 100)}% of order)</span>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Checklist buttons — always visible */}
      <div style={s.checkRow}>
        <button style={s.checkBtn} onClick={() => onChecklist('machine')}>⚙️ Machine</button>
        <button style={s.checkBtn} onClick={() => onChecklist('mould')}>🔲 Mould</button>
        <button style={s.checkBtn} onClick={() => onChecklist('fai')}>🔍 FAI</button>
      </div>

      {/* Logging action buttons — always visible */}
      <div style={s.actionRow}>
        <button
          style={{ ...s.actionBtn, ...(weightOverdue ? s.actionBtnOverdue : {}) }}
          onClick={onOpenWeightModal}
        >
          ⚖ Log Weight
        </button>
        <button style={s.actionBtn} onClick={onOpenInspectModal}>📋 Inspect</button>
        <button style={s.actionBtn} onClick={onOpenScrapModal}>🗑 Log Scrap</button>
      </div>

      {/* Admin change acknowledgement banner */}
      {job.pending_change && (
        <div style={{ margin: '0 14px 10px', background: 'rgba(251,191,36,0.08)', border: '2px solid rgba(251,191,36,0.35)', borderRadius: '8px', padding: '12px 14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#fbbf24', marginBottom: '4px' }}>⚠ Job updated by Admin</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>
            The production director has made changes to this job. Please review the updated schedule and acknowledge.
            {job.pending_change.notes && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>"{job.pending_change.notes}"</div>}
          </div>
          <button
            onClick={onAcknowledge}
            style={{ background: '#fbbf24', color: '#000', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', width: '100%' }}
          >
            ✓ I acknowledge this change
          </button>
        </div>
      )}

      {/* Expanded panel — history + end */}
      {expanded && (
        <div style={s.logPanel}>
          {weights.length > 0 && (
            <div style={s.logSect}>
              <div style={s.logSectTitle}>Weight history</div>
              {weights.slice(0, 5).map(w => (
                <div key={w.id} style={s.logEntry}>
                  <span style={s.logVal}>{Number(w.weight_g).toLocaleString()}g</span>
                  {w.drift_g !== null && (
                    <span style={{ ...s.logMeta, color: Math.abs(w.drift_g) <= 0.5 ? '#86efac' : '#fca5a5' }}>
                      {w.drift_g > 0 ? '+' : ''}{Number(w.drift_g).toLocaleString()}g
                    </span>
                  )}
                  <span style={s.logTime}>{new Date(w.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}

          {inspections.length > 0 && (
            <div style={s.logSect}>
              <div style={s.logSectTitle}>Inspection history</div>
              {inspections.slice(0, 3).map(i => (
                <div key={i.id} style={s.logEntry}>
                  <span style={{ ...s.logVal, color: i.result === 'pass' ? '#86efac' : '#fca5a5' }}>
                    {i.result === 'pass' ? '✓ Pass' : '✗ Fail'}
                  </span>
                  {i.defect_types?.length > 0 && (
                    <span style={s.logMeta}>{i.defect_types.join(', ')}</span>
                  )}
                  <span style={s.logTime}>{new Date(i.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}

          {scraps.length > 0 && (
            <div style={s.logSect}>
              <div style={s.logSectTitle}>Scrap history</div>
              {scraps.slice(0, 3).map(sc => (
                <div key={sc.id} style={s.logEntry}>
                  <span style={{ ...s.logVal, color: '#fca5a5' }}>{sc.count} pcs</span>
                  <span style={s.logMeta}>{sc.reason}</span>
                  <span style={s.logTime}>{new Date(sc.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}

          {weights.length === 0 && inspections.length === 0 && scraps.length === 0 && (
            <div style={{ color: '#4b5563', fontSize: '12px', marginBottom: '12px', textAlign: 'center' }}>
              No entries logged yet
            </div>
          )}

          <button style={s.endBtn} onClick={onEnd}>End Job</button>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, valueColor }) {
  return (
    <div style={s.statBox}>
      <div style={s.statLabel}>{label}</div>
      <div style={{ ...s.statValue, color: valueColor || '#f0f2f5' }}>{value}</div>
    </div>
  );
}

// ── SCHEDULED CARD ────────────────────────────────────────────
function ScheduledCard({ sched, onStart, colours }) {
  const parts = [
    sched.machines?.name,
    sched.recipe?.name || sched.recipe?.material?.name,
    sched.target_qty ? `${sched.target_qty} pcs` : null,
    sched.setter_name ? `Setter: ${sched.setter_name}` : null,
  ].filter(Boolean);
  const mbHex      = getMbHex(sched.recipe, colours);
  const mbBg       = mbHex ? getMbDarkBg(mbHex)     : null;
  const mbText     = mbHex ? getMbTextColor(mbHex)   : null;
  const titleColor = getContrastTitle(mbHex);

  return (
    <div style={{ ...s.schedCard, background: mbBg || '#1e2329' }}>
      <div style={{ flex: 1 }}>
        <div style={{ ...s.schedTitle, color: titleColor }}>
          {sched.moulds?.ref ? `${sched.moulds.ref} — ${sched.moulds.name}` : 'Unspecified mould'}
        </div>
        <div style={{ ...s.schedSub, color: mbText || undefined }}>{parts.join(' · ')}</div>
        {sched.notes && <div style={{ ...s.schedNotes, color: mbText || undefined }}>{sched.notes}</div>}
      </div>
      <button style={s.schedStartBtn} onClick={onStart}>▶ Start</button>
    </div>
  );
}

// ── COMPLETED CARD ────────────────────────────────────────────
function CompletedCard({ job, onClick, onArchive, colours }) {
  const duration = job.ended_at
    ? Math.round((new Date(job.ended_at) - new Date(job.started_at)) / 60000)
    : null;
  const mbHex      = getMbHex(job.recipe, colours);
  const mbBg       = mbHex ? getMbDarkBg(mbHex)     : null;
  const mbText     = mbHex ? getMbTextColor(mbHex)   : null;
  const titleColor = getContrastTitle(mbHex);

  return (
    <div style={{ ...s.doneCard, background: mbBg || '#1e2329' }} onClick={onClick}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ ...s.doneTitle, color: titleColor }}>
            {[job.parts?.name, job.order_number].filter(Boolean).join(' — ') || [job.moulds?.ref, job.moulds?.name].filter(Boolean).join(' — ') || '—'}
          </span>
          {job.golden_run && <span style={s.goldenBadge}>⭐ Golden</span>}
        </div>
        <div style={{ ...s.doneSub, color: mbText || undefined }}>{job.machines?.name} · {job.recipe?.name || job.material?.name}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        {duration !== null && <div style={{ ...s.doneDuration, color: mbText || undefined }}>{duration}m</div>}
        {job.ended_at && (
          <div style={{ ...s.doneTime, color: mbText || undefined }}>{new Date(job.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        )}
        <button style={s.archiveBtn} onClick={onArchive}>Archive</button>
      </div>
    </div>
  );
}

// ── JOB DETAIL MODAL ─────────────────────────────────────────
function JobDetailModal({ job, onClose }) {
  const [weights, setWeights] = useState([]);
  const [scraps, setScraps]   = useState([]);

  useEffect(() => {
    api.get(`/api/v1/jobs/${job.id}/weights`).then(r => setWeights(r.data));
    api.get(`/api/v1/jobs/${job.id}/scrap`).then(r => setScraps(r.data));
  }, [job.id]);

  const totalScrap = scraps.reduce((sum, sc) => sum + sc.count, 0);
  const duration = job.ended_at
    ? Math.round((new Date(job.ended_at) - new Date(job.started_at)) / 60000)
    : null;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>
              {[job.parts?.name, job.order_number].filter(Boolean).join(' — ') || [job.moulds?.ref, job.moulds?.name].filter(Boolean).join(' — ') || '—'}
            </div>
            <div style={s.modalSub}>{job.machines?.name} · {job.recipe?.name || job.material?.name}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.modalStats}>
            <ModalStat label="Status"         value={job.golden_run ? '⭐ Golden run' : job.status} />
            <ModalStat label="Started"        value={new Date(job.started_at).toLocaleString()} />
            {job.ended_at && <ModalStat label="Ended"    value={new Date(job.ended_at).toLocaleString()} />}
            {duration      && <ModalStat label="Duration" value={`${duration} min`} />}
            <ModalStat label="End reason"     value={job.end_reason || '—'} />
            <ModalStat label="Weights logged" value={weights.length} />
            <ModalStat label="Total scrap"    value={`${totalScrap} pcs`} />
            {job.target_weight_g && <ModalStat label="Target weight" value={`${Number(job.target_weight_g).toLocaleString()}g ±${Number(job.weight_tolerance_g).toLocaleString()}g`} />}
          </div>

          {job.recipe && (
            <div style={s.modalSection}>
              <div style={s.sectionTitle}>Recipe — {job.recipe.name}</div>
              {(job.recipe.recipe_components || []).map(c => (
                <div key={c.id} style={s.logEntry}>
                  <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px',
                    background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534', color: '#fff', marginRight: '6px' }}>
                    {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
                  </span>
                  <span style={s.logVal}>{c.material?.name}</span>
                  <span style={s.logMeta}>{c.percentage}%</span>
                </div>
              ))}
            </div>
          )}

          {weights.length > 0 && (
            <div style={s.modalSection}>
              <div style={s.sectionTitle}>Weight Log</div>
              {weights.map(w => (
                <div key={w.id} style={s.logEntry}>
                  <span style={s.logVal}>{Number(w.weight_g).toLocaleString()}g</span>
                  {w.drift_g !== null && (
                    <span style={{ ...s.logMeta, color: Math.abs(w.drift_g) <= 0.5 ? '#86efac' : '#fca5a5' }}>
                      {w.drift_g > 0 ? '+' : ''}{Number(w.drift_g).toLocaleString()}g
                    </span>
                  )}
                  {w.shot_number && <span style={s.logMeta}>Shot {w.shot_number}</span>}
                  <span style={s.logTime}>{new Date(w.logged_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}

          {scraps.length > 0 && (
            <div style={s.modalSection}>
              <div style={s.sectionTitle}>Scrap Log</div>
              {scraps.map(sc => (
                <div key={sc.id} style={s.logEntry}>
                  <span style={{ ...s.logVal, color: '#fca5a5' }}>{sc.count} pcs</span>
                  <span style={s.logMeta}>{sc.reason}</span>
                  {sc.shot_number && <span style={s.logMeta}>Shot {sc.shot_number}</span>}
                  <span style={s.logTime}>{new Date(sc.logged_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalStat({ label, value }) {
  return (
    <div style={s.modalStat}>
      <span style={s.modalStatLabel}>{label}</span>
      <span style={s.modalStatValue}>{value}</span>
    </div>
  );
}

// ── ARCHIVE VIEW ──────────────────────────────────────────────
function ArchiveView({ onSelect }) {
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState('');
  const [machines, setMachines]   = useState([]);
  const [moulds, setMoulds]       = useState([]);
  const [materials, setMaterials] = useState([]);
  const [filters, setFilters]     = useState({
    machine_id: '', mould_id: '', material_id: '', golden_run: false, month: '', year: '',
  });

  useEffect(() => {
    api.get('/api/v1/machines').then(r => setMachines(r.data));
    api.get('/api/v1/moulds').then(r => setMoulds(r.data));
    api.get('/api/v1/materials').then(r => setMaterials(r.data));
    loadArchive({});
  }, []);

  function loadArchive(f) {
    setLoading(true);
    const params = new URLSearchParams();
    if (f.machine_id)  params.set('machine_id',  f.machine_id);
    if (f.mould_id)    params.set('mould_id',    f.mould_id);
    if (f.material_id) params.set('material_id', f.material_id);
    if (f.golden_run)  params.set('golden_run',  'true');
    if (f.month)       params.set('month',       f.month);
    if (f.year)        params.set('year',        f.year);
    api.get(`/api/v1/jobs/archive?${params.toString()}`)
      .then(r => { setJobs(r.data); setLoading(false); setLoadError(''); })
      .catch(err => { setLoadError(err.response?.data?.error || 'Failed to load archive'); setLoading(false); });
  }

  function updateFilter(key, value) {
    const nf = { ...filters, [key]: value };
    setFilters(nf);
    loadArchive(nf);
  }

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);
  const months = [
    { v: '1', l: 'January' }, { v: '2', l: 'February' }, { v: '3', l: 'March' },
    { v: '4', l: 'April' },   { v: '5', l: 'May' },      { v: '6', l: 'June' },
    { v: '7', l: 'July' },    { v: '8', l: 'August' },   { v: '9', l: 'September' },
    { v: '10', l: 'October' },{ v: '11', l: 'November' },{ v: '12', l: 'December' },
  ];

  return (
    <div>
      {loadError && <div style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', margin: '0 0 12px' }}>{loadError}</div>}
      <div style={s.filterBox}>
        <div style={s.filterGrid}>
          <select style={s.filterSel} value={filters.year}        onChange={e => updateFilter('year', e.target.value)}>
            <option value="">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select style={s.filterSel} value={filters.month}       onChange={e => updateFilter('month', e.target.value)}>
            <option value="">All months</option>
            {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select style={s.filterSel} value={filters.machine_id}  onChange={e => updateFilter('machine_id', e.target.value)}>
            <option value="">All machines</option>
            {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select style={s.filterSel} value={filters.mould_id}    onChange={e => updateFilter('mould_id', e.target.value)}>
            <option value="">All moulds</option>
            {moulds.map(m => <option key={m.id} value={m.id}>{m.ref} — {m.name}</option>)}
          </select>
          <select style={s.filterSel} value={filters.material_id} onChange={e => updateFilter('material_id', e.target.value)}>
            <option value="">All materials</option>
            {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <label style={s.filterCheck}>
            <input type="checkbox" checked={filters.golden_run} onChange={e => updateFilter('golden_run', e.target.checked)} />
            {' '}Golden runs only
          </label>
        </div>
      </div>
      {loading ? (
        <div style={s.center}>Loading archive...</div>
      ) : jobs.length === 0 ? (
        <div style={s.emptyBox}>
          <div style={s.emptyText}>No archived jobs found</div>
          <div style={s.emptySub}>Try adjusting the filters</div>
        </div>
      ) : (
        <div style={s.jobList}>
          {jobs.map(job => (
            <div key={job.id} style={{ ...s.doneCard, cursor: 'pointer' }} onClick={() => onSelect(job)}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={s.doneTitle}>{job.moulds?.ref} — {job.moulds?.name}</span>
                  {job.golden_run && <span style={s.goldenBadge}>⭐ Golden</span>}
                </div>
                <div style={s.doneSub}>{job.machines?.name} · {job.recipe?.name || job.material?.name}</div>
                <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>{new Date(job.started_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── JOB WIZARD ────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function JobWizard({ onBack, onStarted }) {
  const { toast } = useToast();
  const [step, setStep]         = useState(1);
  const [machines, setMachines] = useState([]);
  const [moulds, setMoulds]     = useState([]);
  const [recipes, setRecipes]   = useState([]);
  const [selected, setSelected] = useState({ machine: null, mould: null, recipe: null });
  const [targets, setTargets]   = useState({ target_parts: '', target_cycle_s: '', target_weight_g: '', weight_tolerance_g: 0.5, setter_name: '' });
  const [saving, setSaving]     = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const canAddRecipe = useAuth().can('add_recipe');

  useEffect(() => {
    api.get('/api/v1/machines').then(r => setMachines(r.data));
    api.get('/api/v1/moulds').then(r => setMoulds(r.data));
  }, []);

  function selectMachine(m) { setSelected(s => ({ ...s, machine: m })); setStep(2); }
  function selectMould(m) {
    setSelected(s => ({ ...s, mould: m, recipe: null }));
    api.get(`/api/v1/recipes/mould/${m.id}`).then(r => setRecipes(r.data));
    setStep(3);
  }
  function selectRecipe(r) { setSelected(s => ({ ...s, recipe: r })); setStep(4); }

  function startJob() {
    setSaving(true);
    api.post('/api/v1/jobs', {
      machine_id:         selected.machine.id,
      mould_id:           selected.mould.id,
      material_id:        selected.recipe.material_id,
      recipe_id:          selected.recipe.id,
      target_parts:       parseInt(targets.target_parts) || null,
      target_cycle_s:     parseFloat(targets.target_cycle_s) || null,
      target_weight_g:    parseFloat(targets.target_weight_g) || selected.mould.sw_g,
      weight_tolerance_g: parseFloat(targets.weight_tolerance_g) || 0.5,
      setter_name:        targets.setter_name || null,
    })
      .then(r => onStarted(r.data))
      .catch(err => { setSaving(false); toast.error(err.response?.data?.error || 'Failed to start job — please try again'); });
  }

  const stepLabels = ['Machine', 'Mould', 'Recipe', 'Targets'];

  return (
    <div style={s.page}>
      {showRecipeModal && (
        <RecipeCreatorModal
          mouldId={selected.mould?.id}
          onSaved={recipe => { setRecipes(r => [...r, recipe]); setShowRecipeModal(false); selectRecipe(recipe); }}
          onClose={() => setShowRecipeModal(false)}
        />
      )}

      <div style={s.wizardHeader}>
        <button style={s.backBtn} onClick={step === 1 ? onBack : () => setStep(step - 1)}>← Back</button>
        <div>
          <div style={s.title}>New Job</div>
          <div style={s.stepIndicator}>
            {stepLabels.map((label, i) => (
              <span key={label} style={{
                ...s.stepDot,
                background: i + 1 === step ? '#14b8a6' : i + 1 < step ? '#0d9488' : '#2a3040',
                color: i + 1 <= step ? '#fff' : '#6b7280',
              }}>
                {i + 1 < step ? '✓' : i + 1}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={s.stepLabel}>Step {step} of 4 — Select {stepLabels[step - 1]}</div>

      {step === 1 && (
        <div style={s.selectGrid}>
          {machines.map(m => (
            <div key={m.id} style={s.selectCard} onClick={() => selectMachine(m)}>
              <div style={s.selectCardTitle}>{m.name}</div>
              <div style={s.selectCardSub}>{m.site} · {m.tonnes}T · ⌀{m.screw_mm}mm</div>
            </div>
          ))}
        </div>
      )}

      {step === 2 && (
        <div style={s.selectGrid}>
          {moulds.map(m => (
            <div key={m.id} style={s.selectCard} onClick={() => selectMould(m)}>
              <div style={s.selectCardTitle}>{m.ref}</div>
              <div style={s.selectCardSub}>{m.name} · {m.cavities} cav</div>
            </div>
          ))}
        </div>
      )}

      {step === 3 && (
        <div>
          {recipes.length === 0 ? (
            <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '32px', textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px' }}>No recipes for {selected.mould?.ref}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>Create a recipe to define the material blend</div>
              {canAddRecipe
                ? <button style={s.startBtn} onClick={() => setShowRecipeModal(true)}>+ Create Recipe</button>
                : <div style={{ fontSize: '12px', color: '#9ca3af' }}>A setter or a director adds a recipe.</div>}
            </div>
          ) : (
            <>
              <div style={s.selectGrid}>
                {recipes.map(r => (
                  <div key={r.id} style={s.selectCard} onClick={() => selectRecipe(r)}>
                    <div style={s.selectCardTitle}>{r.name}</div>
                    {!(r.recipe_components || []).some(c => c.type === 'base') && (
                      <div style={s.selectCardSub}>{r.material?.name}</div>
                    )}
                    {(r.recipe_components || []).map(c => (
                      <div key={c.id} style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                        <span style={{ fontSize: '8px',
                          background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534',
                          color: '#fff', padding: '1px 4px', borderRadius: '3px', marginRight: '4px' }}>
                          {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
                        </span>
                        {c.material?.name}{c.percentage ? ` · ${c.percentage}%` : ''}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {canAddRecipe && <button style={{ ...s.backBtn, marginTop: '16px', width: '100%', textAlign: 'center' }}
                onClick={() => setShowRecipeModal(true)}>
                + Create new recipe for this mould
              </button>}
            </>
          )}
        </div>
      )}

      {step === 4 && (
        <div>
          <div style={s.summaryBox}>
            <SummaryRow label="Machine"  value={selected.machine?.name} />
            <SummaryRow label="Mould"    value={`${selected.mould?.ref} — ${selected.mould?.name}`} />
            <SummaryRow label="Recipe"   value={selected.recipe?.name} />
            <SummaryRow label="Material" value={selected.recipe?.material?.name} />
          </div>
          <div style={s.targetsBox}>
            <div style={s.stepLabel}>Production Targets</div>
            <div style={s.targetGrid}>
              <TargetField label="Target parts"       value={targets.target_parts}       unit="pcs" onChange={v => setTargets(t => ({ ...t, target_parts: v }))} />
              <TargetField label="Target cycle"       value={targets.target_cycle_s}     unit="s"   onChange={v => setTargets(t => ({ ...t, target_cycle_s: v }))} />
              <TargetField label="Shot weight target" value={targets.target_weight_g || selected.mould?.sw_g} unit="g" onChange={v => setTargets(t => ({ ...t, target_weight_g: v }))} />
              <TargetField label="Weight tolerance ±" value={targets.weight_tolerance_g} unit="g"   onChange={v => setTargets(t => ({ ...t, weight_tolerance_g: v }))} />
            </div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={s.targetLabel}>Setter name</label>
              <input
                type="text"
                placeholder="Who is setting up this job?"
                value={targets.setter_name}
                onChange={e => setTargets(t => ({ ...t, setter_name: e.target.value }))}
                style={s.targetInput}
              />
            </div>
          </div>
          <button
            style={{ ...s.startJobBtn, width: '100%', marginTop: '16px', opacity: saving ? 0.6 : 1 }}
            disabled={saving} onClick={startJob}
          >
            {saving ? 'Starting...' : '▶ Start Job'}
          </button>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={s.summaryRow}>
      <span style={s.summaryLabel}>{label}</span>
      <span style={s.summaryValue}>{value}</span>
    </div>
  );
}

function TargetField({ label, value, unit, onChange }) {
  return (
    <div style={s.targetField}>
      <label style={s.targetLabel}>{label}</label>
      <div style={s.targetInputRow}>
        <input type="number" value={value} onChange={e => onChange(e.target.value)} style={s.targetInput} />
        <span style={s.targetUnit}>{unit}</span>
      </div>
    </div>
  );
}

// ── RECIPE CREATOR MODAL ──────────────────────────────────────
function RecipeCreatorModal({ mouldId, onSaved, onClose }) {
  const [materials, setMaterials] = useState([]);
  const [form, setForm]           = useState({ name: '', description: '' });
  const [bases, setBases]         = useState([{ material_id: '', percentage: '' }]);
  const [components, setComponents] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => { api.get('/api/v1/materials').then(r => setMaterials(r.data)); }, []);

  const baseMaterialList = materials.filter(m => (m.type || 'material') === 'material');
  const masterbatches    = materials.filter(m => m.type === 'masterbatch');
  const additives        = materials.filter(m => m.type === 'additive');

  function updateBase(i, key, value) { setBases(b => b.map((e, idx) => idx !== i ? e : { ...e, [key]: value })); }
  function removeBase(i)             { setBases(b => b.filter((_, idx) => idx !== i)); }

  function addComponent(type) { setComponents(c => [...c, { material_id: '', type, percentage: '' }]); }
  function updateComponent(i, key, value) { setComponents(c => c.map((comp, idx) => idx !== i ? comp : { ...comp, [key]: value })); }
  function removeComponent(i) { setComponents(c => c.filter((_, idx) => idx !== i)); }

  function save() {
    if (!form.name) { setError('Recipe name is required'); return; }
    const validBases = bases.filter(b => b.material_id);
    if (validBases.length === 0) { setError('At least one base material is required'); return; }
    setSaving(true);
    const payload = {
      mould_id:    mouldId,
      material_id: parseInt(validBases[0].material_id),
      name:        form.name,
      description: form.description,
      components: [
        ...validBases.map(b => ({
          material_id: parseInt(b.material_id),
          type:        'base',
          percentage:  b.percentage !== '' ? parseFloat(b.percentage) : null,
        })),
        ...components.filter(c => c.material_id && c.percentage)
          .map(c => ({ material_id: parseInt(c.material_id), type: c.type, percentage: parseFloat(c.percentage) })),
      ],
    };
    api.post('/api/v1/recipes', payload)
      .then(r => onSaved(r.data))
      .catch(err => { setError(err.response?.data?.error || 'Save failed'); setSaving(false); });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>New Recipe</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={s.formField}>
            <label style={s.formLabel}>Recipe name *</label>
            <input style={s.formInput} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Black PP 2% MB" />
          </div>

          {/* Base materials */}
          <div style={{ marginBottom: '12px' }}>
            <div style={s.formLabel}>Base materials *</div>
            <div style={{ marginTop: '8px' }}>
              {bases.map((bm, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={s.baseBadge}>BASE</span>
                  <select style={{ ...s.formInput, flex: 3, minWidth: '140px' }}
                    value={bm.material_id} onChange={e => updateBase(i, 'material_id', e.target.value)}>
                    <option value="">— Select —</option>
                    {baseMaterialList.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <input type="number" placeholder="%" style={{ ...s.formInput, width: '70px', flex: 'none' }}
                    value={bm.percentage} onChange={e => updateBase(i, 'percentage', e.target.value)} />
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
                  {bases.length > 1 && (
                    <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }}
                      onClick={() => removeBase(i)}>×</button>
                  )}
                </div>
              ))}
              <button style={s.addComponentBtn} onClick={() => setBases(b => [...b, { material_id: '', percentage: '' }])}>
                + Add base material
              </button>
            </div>
          </div>

          {/* Masterbatches & Additives */}
          <div>
            <div style={s.formLabel}>Masterbatches & Additives</div>
            <div style={{ marginTop: '8px' }}>
              {components.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ ...s.baseBadge, background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534' }}>
                    {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
                  </span>
                  <select style={{ ...s.formInput, flex: 2, minWidth: '140px' }}
                    value={c.material_id} onChange={e => updateComponent(i, 'material_id', e.target.value)}>
                    <option value="">— Select —</option>
                    {(c.type === 'masterbatch' ? masterbatches : additives).map(m =>
                      <option key={m.id} value={m.id}>{m.name}</option>
                    )}
                  </select>
                  <input type="number" placeholder="%" style={{ ...s.formInput, width: '70px', flex: 'none' }}
                    value={c.percentage} onChange={e => updateComponent(i, 'percentage', e.target.value)} />
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
                  <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }}
                    onClick={() => removeComponent(i)}>×</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button style={s.addComponentBtn} onClick={() => addComponent('masterbatch')}>+ Masterbatch</button>
                <button style={s.addComponentBtn} onClick={() => addComponent('additive')}>+ Additive</button>
              </div>
            </div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving...' : 'Save Recipe'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── END JOB MODAL ─────────────────────────────────────────────
function EndJobModal({ job, weights, scraps, totalScrap, planTotals, onCancel, onEnded }) {
  const { toast } = useToast();
  const [endReason, setEndReason]       = useState('complete');
  const [goldenRun, setGoldenRun]       = useState(false);
  const [goodParts, setGoodParts]       = useState('');
  const [ending, setEnding]             = useState(false);
  // A family mould -- one shot gives several different pieces, each its own
  // stock (the director, 28 Oct 2026: "1000 shots = 1000 sets = 3000 pieces
  // (1000 of each)"): the count asked for is shots, said plainly.
  const [set, setSet]                   = useState([]);
  useEffect(() => {
    if (!job.part_id) return;
    api.get(`/api/v1/stock/auto/set/${job.part_id}`).then(r => setSet(r.data.pieces || [])).catch(() => setSet([]));
  }, [job.part_id]);
  const family = set.length > 1;
  const duration = Math.round((new Date() - new Date(job.started_at)) / 60000);
  const scrapRate = goodParts && totalScrap
    ? Math.round(totalScrap / (parseInt(goodParts) + totalScrap) * 100)
    : null;

  const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
  const shiftStart = new Date(job.started_at) > todayMidnight ? new Date(job.started_at) : todayMidnight;
  const cavities = job.moulds?.cavities || 1;
  const expectedToday = job.target_cycle_s
    ? Math.floor((Date.now() - shiftStart.getTime()) / 1000 / job.target_cycle_s * cavities)
    : null;
  function endJob() {
    setEnding(true);
    api.post(`/api/v1/jobs/${job.id}/end`, {
      end_reason: endReason,
      golden_run: goldenRun,
      good_parts_count: goodParts ? parseInt(goodParts) : null,
    }).then(r => {
      // Its pallets on the truck, for a part sold as it is (2 Nov 2026).
      const truck = (r?.data?.truck_done || []).map(x => ` ${x.pallets} pallet${x.pallets === 1 ? '' : 's'} of ${x.line} on the ${x.order} truck ${x.pallets === 1 ? 'is' : 'are'} now done.`).join('');
      if (truck) toast.success(`Run ended.${truck}`, 8000);
      onEnded();
    });
  }

  return (
    <div style={s.overlay} onClick={onCancel}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>End Job</div>
            <div style={s.modalSub}>{job.moulds?.ref} — {job.moulds?.name}</div>
          </div>
          <button style={s.closeBtn} onClick={onCancel}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.modalSection}>
            <div style={s.sectionTitle}>{family ? 'Good shots produced this session *' : 'Good parts produced this session *'}</div>
            {family && (
              <div style={{ fontSize: '13px', color: '#d1d5db', marginBottom: '10px', lineHeight: 1.5 }}>
                Each shot gives {set.map(x => `${x.per} ${x.name}`).join(' + ')}. Type the shots, not the pieces.
              </div>
            )}
            {(() => {
              const orderTarget = planTotals?.target_parts || job.target_parts;
              const madeSoFar   = planTotals?.total_good_parts ?? null;
              if (!orderTarget && expectedToday == null) return null;
              return (
                <div style={{ background: '#0f1117', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {orderTarget && <span>Order target: <strong style={{ color: '#f0f2f5' }}>{orderTarget.toLocaleString()} pcs</strong></span>}
                  {madeSoFar != null && <span>Made in previous shifts: <strong style={{ color: '#f0f2f5' }}>{madeSoFar.toLocaleString()} pcs</strong></span>}
                  {orderTarget && madeSoFar != null && <span>Remaining after this shift: <strong style={{ color: '#f0f2f5' }}>{Math.max(0, orderTarget - madeSoFar).toLocaleString()} pcs (before entering today's count)</strong></span>}
                  {expectedToday != null && <span>Expected today (cycle time): <strong style={{ color: '#f0f2f5' }}>{expectedToday.toLocaleString()} pcs</strong></span>}
                </div>
              );
            })()}
            <input
              type="number"
              value={goodParts}
              onChange={e => setGoodParts(e.target.value)}
              placeholder="0"
              style={{ ...s.targetInput, fontSize: '28px', fontWeight: '700', textAlign: 'center', padding: '14px' }}
              autoFocus
            />
            {family && goodParts && parseInt(goodParts) > 0 && (
              <div style={{ fontSize: '13px', color: '#14b8a6', marginTop: '6px', textAlign: 'center' }}>
                = {set.map(x => `${(parseInt(goodParts) * x.per).toLocaleString('en-GB')} ${x.name}`).join(', ')}
              </div>
            )}
            {scrapRate !== null && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', textAlign: 'center' }}>
                Scrap rate: <strong style={{ color: scrapRate > 5 ? '#fca5a5' : '#86efac' }}>{scrapRate}%</strong>
                {' '}({totalScrap} scrap / {parseInt(goodParts) + totalScrap} total)
              </div>
            )}
          </div>
          <div style={s.modalStats}>
            <ModalStat label="Machine"        value={job.machines?.name} />
            <ModalStat label="Recipe"         value={job.recipe?.name || job.material?.name} />
            <ModalStat label="Duration"       value={`${duration} min`} />
            <ModalStat label="Weights logged" value={weights.length} />
            <ModalStat label="Total scrap"    value={`${totalScrap} pcs`} />
            {job.target_weight_g && <ModalStat label="Target weight" value={`${Number(job.target_weight_g).toLocaleString()}g ±${Number(job.weight_tolerance_g).toLocaleString()}g`} />}
          </div>
          <div style={s.modalSection}>
            <div style={s.sectionTitle}>End Reason</div>
            <select value={endReason} onChange={e => setEndReason(e.target.value)} style={s.targetInput}>
              <option value="complete">Run complete</option>
              <option value="shift">End of shift</option>
              <option value="machine">Machine issue</option>
              <option value="mould">Mould issue</option>
              <option value="quality">Quality issue</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={s.modalSection}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={goldenRun} onChange={e => setGoldenRun(e.target.checked)} style={{ width: '18px', height: '18px' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#f0f2f5' }}>Mark as golden run</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Process was stable and within all tolerances</div>
              </div>
            </label>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onCancel}>Cancel</button>
          <button style={{ ...s.endConfirmBtn, opacity: ending ? 0.6 : 1 }} disabled={ending} onClick={endJob}>
            {ending ? 'Ending...' : 'Confirm End Job'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SETTER NAME PROMPT ────────────────────────────────────────
function SetterNamePrompt({ sched, onConfirm, onCancel }) {
  const [name, setName] = useState('');
  const title = [sched.parts?.name, sched.order_number].filter(Boolean).join(' — ')
    || [sched.moulds?.ref, sched.moulds?.name].filter(Boolean).join(' — ')
    || 'Job';
  return (
    <div style={s.overlay} onClick={onCancel}>
      <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '2px' }}>Start Job</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{title} · {sched.machines?.name}</div>
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Setter name</label>
            <input
              type="text"
              placeholder="Who is setting up this job?"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onConfirm(name)}
              autoFocus
              style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '10px 12px', fontSize: '14px', fontFamily: 'system-ui' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px', justifyContent: 'space-between' }}>
          <button style={s.backBtn} onClick={onCancel}>Cancel</button>
          <button style={s.schedStartBtn} onClick={() => onConfirm(name)}>▶ Start</button>
        </div>
      </div>
    </div>
  );
}

// ── WEIGHT MODAL ──────────────────────────────────────────────
function WeightModal({ job, weights, onSubmit, onClose }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const lastThree = weights.slice(0, 3);
  const target = job.target_weight_g;
  const drift = value && target ? parseFloat((parseFloat(value) - target).toFixed(2)) : null;

  function submit() {
    if (!value) return;
    setSaving(true);
    const w = parseFloat(value);
    const d = target ? parseFloat((w - target).toFixed(2)) : null;
    onSubmit(w, d);
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Log Shot Weight</div>
            <div style={s.modalSub}>
              {job.parts?.name || job.moulds?.ref}
              {target && <span> · Target: {target}g</span>}
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <input
            type="number"
            inputMode="decimal"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="0.00"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box', background: '#0f1117',
              border: '2px solid #14b8a6', borderRadius: '10px', color: '#f0f2f5',
              fontSize: '40px', fontWeight: '700', textAlign: 'center',
              padding: '16px', fontFamily: 'system-ui',
            }}
          />
          {drift !== null && (
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '15px', fontWeight: '700',
              color: Math.abs(drift) <= (job.weight_tolerance_g || 0.5) ? '#86efac' : '#fca5a5' }}>
              {drift === 0 ? 'On target' : `${Math.abs(drift).toLocaleString()} g ${drift > 0 ? 'over' : 'under'} target`}
            </div>
          )}
          {lastThree.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={s.logSectTitle}>Last {lastThree.length} entries</div>
              {lastThree.map(w => {
                const minsAgo = Math.floor((Date.now() - new Date(w.logged_at).getTime()) / 60000);
                return (
                  <div key={w.id} style={s.logEntry}>
                    <span style={s.logVal}>{Number(w.weight_g).toLocaleString()}g</span>
                    {w.drift_g !== null && (
                      <span style={{ ...s.logMeta, color: Math.abs(w.drift_g) <= 0.5 ? '#86efac' : '#fca5a5' }}>
                        {w.drift_g > 0 ? '+' : ''}{Number(w.drift_g).toLocaleString()}g
                      </span>
                    )}
                    <span style={s.logTime}>{minsAgo === 0 ? 'just now' : `${minsAgo}m ago`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.saveBtn, opacity: (!value || saving) ? 0.5 : 1, padding: '10px 28px', fontSize: '14px' }}
            disabled={!value || saving}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Log Weight'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── INSPECT MODAL ─────────────────────────────────────────────
function InspectModal({ job, onSubmit, onClose }) {
  const [result, setResult]           = useState(null); // 'pass' | 'fail'
  const [selectedDefects, setDefects] = useState([]);
  const [photoUrl, setPhotoUrl]       = useState('');
  const [notes, setNotes]             = useState('');
  const [uploading, setUploading]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  function toggleDefect(d) {
    setDefects(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  }

  async function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, 'inspection-photos');
      setPhotoUrl(url);
    } catch (err) {
      setError(err.message || 'Photo upload failed');
    }
    setUploading(false);
  }

  function submit() {
    if (!result) return;
    if (result === 'fail' && !photoUrl) { setError('A photo is required for failed inspections'); return; }
    setSaving(true);
    onSubmit({ result, defect_types: selectedDefects, photo_url: photoUrl || null, notes: notes || null });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Visual Inspection</div>
            <div style={s.modalSub}>{job.parts?.name || job.moulds?.ref}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

          <div style={s.logSectTitle}>Result</div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <button
              style={{ flex: 1, padding: '16px', fontSize: '15px', fontWeight: '700', borderRadius: '10px', border: '2px solid',
                borderColor: result === 'pass' ? '#22c55e' : '#2a3040',
                background: result === 'pass' ? '#14532d' : '#0f1117', color: '#f0f2f5', cursor: 'pointer' }}
              onClick={() => setResult('pass')}
            >✓ Pass</button>
            <button
              style={{ flex: 1, padding: '16px', fontSize: '15px', fontWeight: '700', borderRadius: '10px', border: '2px solid',
                borderColor: result === 'fail' ? '#ef4444' : '#2a3040',
                background: result === 'fail' ? '#7f1d1d' : '#0f1117', color: '#f0f2f5', cursor: 'pointer' }}
              onClick={() => setResult('fail')}
            >✗ Fail</button>
          </div>

          {result === 'fail' && (
            <>
              <div style={s.logSectTitle}>Defect types (select all that apply)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '6px', marginBottom: '16px' }}>
                {DEFECT_TYPES.map(d => (
                  <button key={d}
                    style={{ padding: '10px 8px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', border: '1px solid',
                      borderColor: selectedDefects.includes(d) ? '#ef4444' : '#2a3040',
                      background: selectedDefects.includes(d) ? '#7f1d1d' : '#0f1117', color: '#f0f2f5', cursor: 'pointer', textAlign: 'center' }}
                    onClick={() => toggleDefect(d)}
                  >{d}</button>
                ))}
              </div>

              <div style={s.logSectTitle}>Photo (required) *</div>
              {photoUrl
                ? <div style={{ marginBottom: '12px' }}>
                    <img src={photoUrl} alt="inspection" style={{ width: '100%', borderRadius: '8px', maxHeight: '200px', objectFit: 'cover' }} />
                    <button style={{ ...s.backBtn, marginTop: '6px', fontSize: '11px' }} onClick={() => setPhotoUrl('')}>Remove photo</button>
                  </div>
                : <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', background: '#0f1117', border: '1px dashed #4b5563', borderRadius: '8px', padding: '16px', textAlign: 'center', cursor: 'pointer', color: '#9ca3af', fontSize: '13px' }}>
                      {uploading ? 'Uploading…' : '📷 Tap to take photo or choose file'}
                      <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
                    </label>
                  </div>
              }
            </>
          )}

          <div style={s.logSectTitle}>Notes (optional)</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any additional observations…"
            style={{ ...s.targetInput, minHeight: '60px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.saveBtn, opacity: (!result || saving) ? 0.5 : 1, padding: '10px 24px' }}
            disabled={!result || saving}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SCRAP MODAL ───────────────────────────────────────────────
function ScrapModal({ job, onSubmit, onClose }) {
  const [defect, setDefect]       = useState('');
  const [count, setCount]         = useState('');
  const [photoUrl, setPhotoUrl]   = useState('');
  const [notes, setNotes]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  async function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, 'scrap-photos');
      setPhotoUrl(url);
    } catch (err) {
      setError(err.message || 'Photo upload failed');
    }
    setUploading(false);
  }

  function submit() {
    if (!defect || !count || !photoUrl) {
      setError('Defect type, count and photo are required');
      return;
    }
    if (defect === 'Other' && !notes.trim()) {
      setError('Notes are required when defect type is Other');
      return;
    }
    setSaving(true);
    onSubmit({ reason: defect, count: parseInt(count), photo_url: photoUrl, notes: notes || null });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Log Scrap</div>
            <div style={s.modalSub}>{job.parts?.name || job.moulds?.ref}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

          <div style={s.logSectTitle}>Defect type</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '6px', marginBottom: '16px' }}>
            {DEFECT_TYPES.map(d => (
              <button key={d}
                style={{ padding: '10px 8px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', border: '1px solid',
                  borderColor: defect === d ? '#f59e0b' : '#2a3040',
                  background: defect === d ? '#292107' : '#0f1117', color: '#f0f2f5', cursor: 'pointer', textAlign: 'center' }}
                onClick={() => setDefect(d)}
              >{d}</button>
            ))}
          </div>

          <div style={s.logSectTitle}>Parts scrapped</div>
          <input
            type="number"
            inputMode="numeric"
            value={count}
            onChange={e => setCount(e.target.value)}
            placeholder="0"
            style={{ width: '100%', boxSizing: 'border-box', background: '#0f1117', border: '2px solid #f59e0b',
              borderRadius: '10px', color: '#f0f2f5', fontSize: '36px', fontWeight: '700',
              textAlign: 'center', padding: '12px', fontFamily: 'system-ui', marginBottom: '16px' }}
          />

          <div style={s.logSectTitle}>Photo (required) *</div>
          {photoUrl
            ? <div style={{ marginBottom: '12px' }}>
                <img src={photoUrl} alt="scrap" style={{ width: '100%', borderRadius: '8px', maxHeight: '180px', objectFit: 'cover' }} />
                <button style={{ ...s.backBtn, marginTop: '6px', fontSize: '11px' }} onClick={() => setPhotoUrl('')}>Remove</button>
              </div>
            : <label style={{ display: 'block', background: '#0f1117', border: '1px dashed #4b5563', borderRadius: '8px', padding: '16px', textAlign: 'center', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', marginBottom: '12px' }}>
                {uploading ? 'Uploading…' : '📷 Tap to take photo or choose file'}
                <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
              </label>
          }

          <div style={s.logSectTitle}>Notes {defect === 'Other' ? <span style={{ color: '#fca5a5' }}>*</span> : '(optional)'}</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={defect === 'Other' ? 'Describe the defect…' : 'Any additional notes…'}
            style={{ ...s.targetInput, minHeight: '56px', resize: 'vertical', width: '100%', boxSizing: 'border-box', border: defect === 'Other' && !notes.trim() ? '1px solid rgba(239,68,68,0.5)' : undefined }}
          />
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.saveBtn, background: '#f59e0b', opacity: (!defect || !count || saving) ? 0.5 : 1, padding: '10px 24px' }}
            disabled={!defect || !count || saving}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Log Scrap'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const s = {
  page:           { padding: '20px', fontFamily: 'system-ui, sans-serif', background: '#0f1117', minHeight: '100vh', color: '#f0f2f5' },
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' },
  title:          { fontSize: '22px', fontWeight: '700', margin: 0 },
  sub:            { fontSize: '12px', color: '#6b7280', margin: '2px 0 0' },
  startBtn:       { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  tabRow:         { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #2a3040' },
  tabBtn:         { background: 'none', border: 'none', color: '#6b7280', fontSize: '13px', fontWeight: '600', padding: '8px 16px', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-1px' },
  tabBtnActive:   { color: '#f0f2f5', borderBottom: '2px solid #14b8a6' },
  section:        { marginBottom: '28px' },
  sectionLabel:   { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6b7280', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' },
  countBadge:     { background: '#2a3040', color: '#9ca3af', fontSize: '10px', fontWeight: '700', padding: '1px 7px', borderRadius: '10px' },
  center:         { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#6b7280' },
  emptyBox:       { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '40px 24px', textAlign: 'center' },
  emptyText:      { fontSize: '15px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' },
  emptySub:       { fontSize: '12px', color: '#4b5563' },

  // Running card
  runCard:        { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', marginBottom: '10px', overflow: 'hidden' },
  runCardTop:     { display: 'flex', gap: '12px', padding: '14px 16px', cursor: 'pointer', alignItems: 'flex-start' },
  carryBadge:     { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f59e0b', background: '#292107', border: '1px solid #92400e', borderRadius: '4px', padding: '2px 6px', marginBottom: '6px', display: 'inline-block' },
  runTitle:       { fontSize: '17px', fontWeight: '700', marginBottom: '4px', lineHeight: 1.2 },
  runSub:         { fontSize: '12px', color: '#6b7280' },
  runningBadge:   { background: '#166534', color: '#86efac', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '8px', whiteSpace: 'nowrap' },
  chevron:        { fontSize: '10px', color: '#6b7280' },
  statsRow:       { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', background: '#2a3040', borderTop: '1px solid #2a3040', borderBottom: '1px solid #2a3040' },
  statBox:        { background: '#1e2329', padding: '10px 12px' },
  statLabel:      { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', marginBottom: '3px', letterSpacing: '0.05em' },
  statValue:      { fontSize: '17px', fontWeight: '700' },
  checkRow:       { display: 'flex', gap: '6px', padding: '10px 14px', flexWrap: 'wrap' },
  checkBtn:       { flex: 1, minWidth: '80px', background: '#0f1117', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '7px', padding: '7px 6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' },
  weightDueBadge: { margin: '0 14px 0', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '700', color: '#f59e0b', animation: 'optimise-glow 2s ease-in-out infinite' },
  actionRow:      { display: 'flex', gap: '6px', padding: '0 14px 10px', flexWrap: 'wrap' },
  actionBtn:      { flex: 1, minWidth: '90px', background: '#0f1117', border: '1px solid #14b8a6', color: '#14b8a6', borderRadius: '7px', padding: '9px 6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', textAlign: 'center' },
  actionBtnOverdue: { borderColor: '#f59e0b', color: '#f59e0b', animation: 'optimise-glow 2s ease-in-out infinite' },
  logPanel:       { padding: '14px 16px', borderTop: '1px solid #2a3040' },
  logSect:        { marginBottom: '14px' },
  logSectTitle:   { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.08em', marginBottom: '8px' },
  logRow:         { display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' },
  logInput:       { flex: 1, background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '14px', minWidth: '60px' },
  logBtn:         { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  logEntry:       { display: 'flex', gap: '10px', alignItems: 'center', padding: '5px 0', borderTop: '1px solid #2a3040' },
  logVal:         { fontSize: '13px', fontWeight: '700', minWidth: '52px' },
  logMeta:        { fontSize: '12px', color: '#6b7280', flex: 1 },
  logTime:        { fontSize: '11px', color: '#4b5563', whiteSpace: 'nowrap' },
  endBtn:         { background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: '8px', padding: '11px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', width: '100%', marginTop: '6px' },

  // Scheduled card
  schedCard:      { background: '#1e2329', border: '1px solid #1d4ed8', borderRadius: '10px', padding: '12px 14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' },
  schedTitle:     { fontSize: '14px', fontWeight: '700', marginBottom: '3px' },
  schedSub:       { fontSize: '11px', color: '#6b7280' },
  schedNotes:     { fontSize: '11px', color: '#9ca3af', marginTop: '3px', fontStyle: 'italic' },
  schedStartBtn:  { background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },

  // Completed card
  doneCard:       { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '12px 14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
  doneTitle:      { fontSize: '14px', fontWeight: '700', marginBottom: '3px' },
  doneSub:        { fontSize: '11px', color: '#6b7280' },
  doneDuration:   { fontSize: '13px', fontWeight: '700', color: '#9ca3af' },
  doneTime:       { fontSize: '11px', color: '#6b7280' },
  goldenBadge:    { fontSize: '10px', fontWeight: '700', background: '#1c1917', color: '#fde68a', border: '1px solid #92400e', borderRadius: '5px', padding: '1px 6px' },
  archiveBtn:     { background: 'none', border: '1px solid #2a3040', color: '#6b7280', borderRadius: '6px', padding: '3px 9px', fontSize: '10px', cursor: 'pointer' },

  // Job list (archive)
  jobList:        { display: 'flex', flexDirection: 'column', gap: '0' },

  // Modals
  overlay:        { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:          { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' },
  modalTitle:     { fontSize: '17px', fontWeight: '700', marginBottom: '4px' },
  modalSub:       { fontSize: '12px', color: '#6b7280' },
  closeBtn:       { background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  modalBody:      { padding: '16px 20px' },
  modalFooter:    { padding: '16px 20px', borderTop: '1px solid #2a3040', display: 'flex', justifyContent: 'space-between' },
  modalStats:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' },
  modalStat:      { display: 'flex', flexDirection: 'column', gap: '2px' },
  modalStatLabel: { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' },
  modalStatValue: { fontSize: '13px', fontWeight: '600' },
  modalSection:   { marginBottom: '16px' },
  sectionTitle:   { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.08em', marginBottom: '8px' },

  // Wizard
  wizardHeader:   { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' },
  stepLabel:      { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: '12px', marginTop: '8px' },
  stepIndicator:  { display: 'flex', gap: '6px', marginTop: '6px' },
  stepDot:        { width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700' },
  selectGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' },
  selectCard:     { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px', cursor: 'pointer' },
  selectCardTitle:{ fontSize: '13px', fontWeight: '700', marginBottom: '4px' },
  selectCardSub:  { fontSize: '11px', color: '#6b7280' },
  summaryBox:     { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px', marginBottom: '12px' },
  summaryRow:     { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2a3040' },
  summaryLabel:   { fontSize: '11px', color: '#6b7280' },
  summaryValue:   { fontSize: '12px', fontWeight: '600' },
  targetsBox:     { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '16px' },
  targetGrid:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  targetField:    { display: 'flex', flexDirection: 'column', gap: '4px' },
  targetLabel:    { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' },
  targetInputRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  targetInput:    { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px', width: '100%' },
  targetUnit:     { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' },
  startJobBtn:    { background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  baseBadge:      { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: '#0f766e', color: '#fff', flexShrink: 0 },
  backBtn:        { background: '#1e2329', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer' },
  endConfirmBtn:  { background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  saveBtn:        { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },

  // Archive filters
  filterBox:      { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px', marginBottom: '16px' },
  filterGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', alignItems: 'center' },
  filterSel:      { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '12px', width: '100%' },
  filterCheck:    { fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },

  // Recipe form
  formField:      { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  formLabel:      { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  formInput:      { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px' },
  addComponentBtn:{ background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer' },
};
